/**
 * Threat data: candidates and confirmed threat events from backend rings.
 * Aligned with arbitium blueprints (analysis_status + outcome split).
 */

import { fetchRing } from '../core';

/** Workflow phase of lab_batch (prefer over legacy status alone). */
export type CandidateAnalysisStatus = 'pending' | 'analyzing' | 'completed' | 'error';

/** Terminal verdict when analysis_status=completed. */
export type CandidateOutcome =
  | 'threat'
  | 'not_threat'
  | 'false_positive'
  | 'duplicated'
  | 'escalated';

/** Legacy + current status values that may still appear on documents. */
export type CandidateStatus =
  | 'pending'
  | 'analyzing'
  | 'not_threat'
  | 'threat'
  | 'error'
  | 'false_positive'
  | 'duplicated'
  | 'escalated'
  /** @deprecated prefer outcome=false_positive */
  | 'anomaly';

export type CandidatePipelineBucket =
  | 'pending'
  | 'analyzing'
  | 'error'
  | 'threat'
  | 'not_threat'
  | 'false_positive'
  | 'duplicated'
  | 'escalated'
  | 'other';

export interface ThreatEventCandidateItem {
  _id: string;
  s3_uri?: string;
  source?: string;
  source_id?: string;
  normalize_mode?: string;
  /** Prefer analysis_status + outcome for UI. */
  status?: CandidateStatus | string;
  analysis_status?: CandidateAnalysisStatus | string;
  outcome?: CandidateOutcome | string;
  analysis_started_at?: string;
  first_processed_at?: string;
  failed_step?: string;
  error_message?: string;
  duplicate_of_candidate_id?: string;
  duplicate_security_event_ids?: unknown;
  dedup_new_event_count?: number;
  dedup_duplicate_event_count?: number;
  anomaly_methods?: unknown;
  llm_skipped?: boolean;
  llm_skipped_reason?: string;
  decision_reasoning?: string;
  knn_closest_category?: string;
  knn_distance?: number;
  min_distance?: number;
  max_distance?: number;
  dnn_predicted_class?: string;
  dnn_max_probability?: number;
  detected_at?: string;
  processed_at?: string;
  [key: string]: unknown;
}

export interface ThreatEventItem {
  _id: string;
  candidate_id: string;
  provider?: string;
  detection_classification?: string;
  detection_description?: string;
  detection_risk_level?: string;
  detection_status?: 'active' | 'in_progress' | 'remediated' | 'error' | 'folded' | string;
  folded?: boolean;
  reopened?: boolean;
  detection_outcome?: string;
  created_at?: string;
  first_event_time?: string;
  last_event_time?: string;
  formed_at?: string;
  assigned_at?: string;
  decided_at?: string;
  verified_at?: string;
  is_test?: boolean;
  is_campaign?: boolean;
  security_event_ids?: unknown;
  links?: unknown;
  [key: string]: unknown;
}

/**
 * Map a candidate to a single pipeline bucket for Alert Flow / metrics.
 * Prefer analysis_status + outcome; fall back to legacy status (incl. anomaly → FP).
 */
export function resolveCandidatePipelineBucket(
  candidate: Pick<ThreatEventCandidateItem, 'analysis_status' | 'outcome' | 'status'>
): CandidatePipelineBucket {
  const analysis = String(candidate.analysis_status || '').toLowerCase().trim();
  const outcome = String(candidate.outcome || '').toLowerCase().trim();
  const status = String(candidate.status || '').toLowerCase().trim();

  if (analysis === 'analyzing' || status === 'analyzing') return 'analyzing';
  if (analysis === 'error' || status === 'error') return 'error';
  if (analysis === 'pending' || (status === 'pending' && !outcome)) return 'pending';

  if (outcome === 'threat' || status === 'threat') return 'threat';
  if (outcome === 'not_threat' || status === 'not_threat') return 'not_threat';
  if (
    outcome === 'false_positive' ||
    status === 'false_positive' ||
    status === 'anomaly'
  ) {
    return 'false_positive';
  }
  if (outcome === 'duplicated' || status === 'duplicated') return 'duplicated';
  if (outcome === 'escalated' || status === 'escalated') return 'escalated';

  if (analysis === 'completed' && !outcome) {
    if (status === 'threat') return 'threat';
    if (status === 'not_threat') return 'not_threat';
    if (status === 'false_positive' || status === 'anomaly') return 'false_positive';
    if (status === 'duplicated') return 'duplicated';
    if (status === 'escalated') return 'escalated';
  }

  return 'other';
}

/** Human-facing label for tables / feed details (parity with Lab Batch UI). */
export function candidateDisplayStatus(
  candidate: Pick<ThreatEventCandidateItem, 'analysis_status' | 'outcome' | 'status'>
): string {
  const outcome = String(candidate.outcome || '').trim();
  if (outcome) return outcome;
  const analysis = String(candidate.analysis_status || '').toLowerCase().trim();
  if (analysis === 'analyzing') return 'analyzing';
  if (analysis === 'pending') return 'pending';
  if (analysis === 'error') return 'error';
  const status = String(candidate.status || '').trim();
  if (status === 'anomaly') return 'false_positive';
  return status || analysis || '—';
}

const RING_CANDIDATES = 'arbitium_threat_event_candidates';
const RING_EVENTS = 'arbitium_threat_events';

/**
 * Fetches threat event candidates from the backend ring.
 */
export async function getThreatCandidates(): Promise<ThreatEventCandidateItem[]> {
  const res = await fetchRing<ThreatEventCandidateItem>(RING_CANDIDATES);
  return Array.isArray(res.items) ? res.items : [];
}

/**
 * Fetches threat event candidates from the backend ring (all pages).
 * Useful for time-window + join computations on the frontend.
 */
export async function getThreatCandidatesAll(): Promise<ThreatEventCandidateItem[]> {
  const res = await fetchRing<ThreatEventCandidateItem>(RING_CANDIDATES, { all: true });
  return Array.isArray(res.items) ? res.items : [];
}

/**
 * Fetches threat events (confirmed threats) from the backend ring.
 */
export async function getThreatEvents(): Promise<ThreatEventItem[]> {
  const res = await fetchRing<ThreatEventItem>(RING_EVENTS);
  return Array.isArray(res.items) ? res.items : [];
}

/**
 * Fetches threat events (confirmed threats) from the backend ring (all pages).
 * Useful for time-window + join computations on the frontend.
 */
export async function getThreatEventsAll(): Promise<ThreatEventItem[]> {
  const res = await fetchRing<ThreatEventItem>(RING_EVENTS, { all: true });
  return Array.isArray(res.items) ? res.items : [];
}

